import type { ReactElement } from "react";
import { useCallback, useEffect, useMemo, useRef } from "react";

import { type DragSourceMonitor, type DropTargetMonitor, useDrag, useDrop } from "react-dnd";
import { getEmptyImage } from "react-dnd-html5-backend";
import { CSSTransition } from "react-transition-group";
import type { CSSTransitionProps } from "react-transition-group/CSSTransition";

import type { BaseSelectionOption } from "../../types";

import { FadeContainer, StyledDraggableContainer } from "./styles";

type DraggableColumnOptionFadeTransitionColumnOptionFadeTransition = Omit<CSSTransitionProps, "addEndListener"> & {
    children: ReactElement;
    findCol(name: string): { col: BaseSelectionOption; index: number };
    moveCol(draggedId: string, overIndex: number): void;
    setParentHasChildDraggin: (hasChildDraggin: boolean) => void;
};

type Item = {
    id: string;
    originalIndex: number;
    /** Measured at drag start so the drag layer, which renders outside the list, can match the row. */
    width: number;
};

export const DraggableColumnOptionFadeTransition = (
    props: DraggableColumnOptionFadeTransitionColumnOptionFadeTransition,
) => {
    const { id, findCol, moveCol, setParentHasChildDraggin } = props;
    const originalIndex = useMemo(() => findCol(id).index, [findCol, id]);
    const handleDropEnd = useCallback(
        (item: Item, monitor: DragSourceMonitor) => {
            const { id: droppedId, originalIndex } = item;
            const didDrop = monitor.didDrop();
            if (!didDrop) {
                moveCol(droppedId, originalIndex);
            }
        },
        [moveCol],
    );

    const handleHover = useCallback(
        ({ id: draggedId }: { id: string }, monitor: DropTargetMonitor) => {
            const { index: overIndex } = findCol(id);
            // monitor.isOver({ shallow: true }) avoid firing the move events twice since the item is nested
            if (draggedId !== id && monitor.isOver({ shallow: true })) {
                moveCol(draggedId, overIndex);
            }
        },
        [findCol, id, moveCol],
    );

    const [{ isDragging }, drag, preview] = useDrag(
        () => ({
            type: "card",
            item: () => ({ id, originalIndex, width: nodeRef.current?.offsetWidth ?? 0 }),
            collect: (monitor) => ({
                isDragging: monitor.isDragging(),
            }),
            end: handleDropEnd,
        }),
        [id, originalIndex, handleDropEnd],
    );

    const [, drop] = useDrop(
        () => ({
            accept: "card",
            hover: handleHover,
        }),
        [handleHover],
    );

    const nodeRef = useRef<HTMLDivElement | null>(null);

    /**
     * The native drag image is a rasterised snapshot on an opaque backdrop, so it cannot keep the
     * card's rounded corners and it traps the row's bottom margin. Suppressing it hands the ghost to
     * ColumnOptionDragLayer, which draws the real card instead.
     */
    useEffect(() => {
        preview(getEmptyImage(), { captureDraggingState: true });
    }, [preview]);

    useEffect(() => {
        setParentHasChildDraggin(isDragging);
    }, [isDragging, setParentHasChildDraggin]);

    return (
        <CSSTransition
            timeout={300}
            classNames="fade"
            {...props}
            nodeRef={nodeRef}
        >
            <StyledDraggableContainer
                isDragging={isDragging}
                ref={(node) => {
                    drag(drop(node));
                }}
            >
                <FadeContainer ref={nodeRef}>{props.children}</FadeContainer>
            </StyledDraggableContainer>
        </CSSTransition>
    );
};
